<template>
	<LazyLoadWrapper>
		<NuxtLoadingIndicator />
		<SharedModal />
	</LazyLoadWrapper>
	<main v-if="renderComponent">
		<LayoutLoginGateHeader v-if="isLoginGateScreen" />
		<Header v-else />
		<!-- Stable single-node container for the async multi-root pages: even
			if a hydration mismatch shifts sibling anchors, late-resolving page
			content can only be inserted here — never after the footer.
			`contents` keeps the layout identical to a direct child. -->
		<div class="contents">
			<slot />
		</div>
		<Footer v-if="!isLoginGateScreen" />
		<CookieConsentBanner />
		<CookieConsentSettingsModal />
		<LazyLoadWrapper>
			<SpeedInsights />
		</LazyLoadWrapper>
	</main>
</template>

<script setup lang="ts">
import { nextTick, ref, provide } from "vue";
import { SpeedInsights } from "@vercel/speed-insights/nuxt";
import LazyLoadWrapper from "~/components/LazyLoadWrapper.vue";

const renderComponent = ref(true);
const isLoginGateScreen = useLoginGateScreen();

const forceRender: () => Promise<void> = async () => {
	renderComponent.value = false;
	await nextTick();
	renderComponent.value = true;
};

provide("forceRender", forceRender);

const nuxtApp = useNuxtApp();
const loading = ref(true);

nuxtApp.hook("app:suspense:resolve", () => {
	loading.value = false;
});

const { locale } = useI18n();
const appStore = useAppStore();
const { initLayoutData } = useLayoutInit();
// `useCartInit` has to be called from setup — it captures Nuxt composables.
const { initCart } = useCartInit();

// Through useAsyncData so the bootstrap runs ONCE, on the server, and hydration
// reads it from the payload. A bare `await` here re-ran the whole thing in the
// browser — layout story, category tree, pricelists, session, menu, application,
// parametrics info — and rewrote every store underneath the rendered page.
// Don't abort SSR on fetch failure — keeps error.vue from rendering empty.
await useAsyncData(`layout-init:${locale.value}`, async () => {
	try {
		await initLayoutData();
	} catch (err) {
		console.error("[layouts/default] initLayoutData failed:", err);
	}

	return true;
});

// The two pieces the server genuinely can't do, run once on the client with the
// data the bootstrap already produced — no refetch:
//  - setColors writes CSS custom properties on `document` (no-op during SSR);
//  - the basket id lives in localStorage, so the cart resolves in the browser.
onMounted(async () => {
	setColors(appStore.colors);

	try {
		await initCart();
	} catch (err) {
		console.error("[layouts/default] initCart failed:", err);
	}
});

useSiteSchemaOrg();
</script>
